import {
  type DatabaseView,
  DEFAULT_DATABASE_VIEW_SETTINGS
} from '@databases/types/database/view';
import { isEqual, pickBy } from 'lodash-es';
import { proxy, snapshot, subscribe } from 'valtio';
import { devtools } from 'valtio/utils';

export const viewState = proxy<{ view: DatabaseView }>({
  view: {
    id: '',
    name: '',
    settings: DEFAULT_DATABASE_VIEW_SETTINGS
  }
});

export const setView = (view: DatabaseView) => {
  viewState.view = view;
};

let cachedView: DatabaseView | undefined;
let unsubscribe: (() => void) | undefined;

/**
 * Compares objects removing properties which have value 'undefined'.
 * This is a duplicate function to avoid circular dependency with @databases/utils.
 */
function isEqualOmitUndefined<T>(value: T, other: T): boolean {
  return isEqual(pickBy(value as any, undefined), pickBy(other as any, undefined));
}

/**
 * Returns a cached snapshot of the current database view settings.
 *
 * **Why this exists:**
 * `snapshot()` creates a new object reference every time it's called, even when data hasn't changed.
 * This breaks Redux selector memoization (re-reselect), causing unnecessary recalculations.
 * This function caches the snapshot and updates it via subscription, maintaining referential equality.
 *
 * **Implementation note:**
 * Uses lazy initialization to avoid test failures when valtio is mocked at module load time.
 */
export const getCachedViewSnapshot = (): DatabaseView => {
  if (!unsubscribe && subscribe) {
    unsubscribe = subscribe(viewState.view, () => {
      cachedView = snapshot(viewState.view) as DatabaseView;
    });
  }

  const currentView = snapshot(viewState.view) as DatabaseView;

  if (!cachedView) {
    cachedView = currentView;
    return cachedView;
  }

  if (
    cachedView.id !== currentView.id ||
    !isEqualOmitUndefined(cachedView.settings, currentView.settings)
  ) {
    cachedView = currentView;
  }

  return cachedView;
};

if (process.env.NODE_ENV === 'development') {
  devtools(viewState, 'Valtio View State');
}

let selectionsCachedView: DatabaseView | undefined;
let selectionsCacheUnsubscribe: (() => void) | undefined;

/**
 * In the selections API, when we build the adapter or actions for the action bar / context menu,
 * we need the most up to date state that busts whenever anything on the entire viewState changes.
 */
export const getCachedViewSnapshotForSelections = (): DatabaseView => {
  if (!selectionsCacheUnsubscribe && subscribe) {
    selectionsCacheUnsubscribe = subscribe(viewState, () => {
      selectionsCachedView = snapshot(viewState.view) as DatabaseView;
    });
  }

  const currentView = snapshot(viewState.view) as DatabaseView;

  if (
    !selectionsCachedView ||
    selectionsCachedView.id !== currentView.id ||
    selectionsCachedView.name !== currentView.name ||
    !isEqualOmitUndefined(selectionsCachedView.settings, currentView.settings)
  ) {
    selectionsCachedView = currentView;
  }

  return selectionsCachedView;
};

/**
 * Notifies immediately after any active-view state transaction. Consumers read
 * and compare the resulting projection, so this deliberately does not encode a
 * list of layout-specific settings here.
 */
export function subscribeViewProjectionChanges(onChange: VoidFunction): VoidFunction {
  return subscribe(viewState, onChange);
}

/**
 * Changes reported by the view-state publisher. This describes viewState only;
 * Bootstrap independently compares the resolved renderer layout for extension views.
 */
export interface ViewIdentityChanges {
  /**
   * The selected viewState.view.id changed, independently of its settings.
   */
  viewIdChanged: boolean;

  /**
   * viewState.view.settings.layout.type changed. This projected value can differ
   * from the persisted extension layout, so it does not identify the renderer.
   */
  layoutTypeChanged: boolean;
}

/**
 * Reports view ID and layout type changes separately so bootstrap can rebuild
 * only modules whose dependencies changed.
 */
export function subscribeViewIdentityChanges(
  onChange: (changes: ViewIdentityChanges) => void
): VoidFunction {
  const readIdentity = () => {
    const v = snapshot(viewState.view) as DatabaseView;

    return { id: v.id, layoutType: v.settings.layout.type };
  };

  let prev = readIdentity();

  return subscribe(viewState, () => {
    const next = readIdentity();

    const changes: ViewIdentityChanges = {
      viewIdChanged: prev.id !== next.id,
      layoutTypeChanged: prev.layoutType !== next.layoutType
    };

    if (changes.viewIdChanged || changes.layoutTypeChanged) {
      prev = next;
      onChange(changes);
    }
  });
}
